import {
  collection, doc, onSnapshot, query, runTransaction, serverTimestamp, setDoc, where,
} from 'firebase/firestore';

export function validateMembership(batch, recordIds) {
  const expected = new Set(batch.records.map((record) => record.id));
  if (!Array.isArray(recordIds) || recordIds.length !== expected.size
    || new Set(recordIds).size !== expected.size || recordIds.some((id) => !expected.has(id))) {
    throw new Error('Existing inventory update has different records; manual review required');
  }
}

export function createFirestoreTransport(firestore, organizationId) {
  const path = `organizations/${organizationId}/apps/inventory/inventories`;
  const inventoryRef = (batch) => doc(firestore, `${path}/${batch.inventoryId}`);
  const updateRef = (batch) => doc(inventoryRef(batch), 'inventoryUpdates', batch.id);
  const watchPublished = (batch, published, error) => {
    const ref = updateRef(batch);
    return onSnapshot(ref, { includeMetadataChanges: true }, (snap) => {
      if (snap.exists() && !snap.metadata.hasPendingWrites && !snap.metadata.fromCache) {
        published(snap.data().recordIds);
      }
    }, error);
  };
  return {
    validateMembership,
    watchPublished,
    writeRecord: (batch, record) => setDoc(doc(inventoryRef(batch), 'records', record.id), record.data),
    publish: (batch) => runTransaction(firestore, async (transaction) => {
      const ref = updateRef(batch);
      const existing = await transaction.get(ref);
      if (existing.exists()) {
        validateMembership(batch, existing.data().recordIds);
        return;
      }
      transaction.set(ref, {
        status: 'pending', recordIds: batch.records.map((r) => r.id), createdAt: serverTimestamp(),
      });
    }),
    watch: (batch, handlers) => {
      // One query per batch, not one listener per record. Metadata events also
      // recover acknowledgements after a reload; promises alone cannot do that.
      const stopRecords = onSnapshot(
        query(collection(inventoryRef(batch), 'records'), where('updateBatchId', '==', batch.id)),
        { includeMetadataChanges: true },
        (snap) => handlers.records({
          fromCache: snap.metadata.fromCache,
          presentIds: snap.docs.map((d) => d.id),
          confirmedIds: snap.docs.filter((d) => !d.metadata.hasPendingWrites).map((d) => d.id),
        }),
        handlers.error,
      );
      const stopUpdate = watchPublished(batch, handlers.published, handlers.error);
      return () => { stopRecords(); stopUpdate(); };
    },
  };
}
